// src/utils/OrderManagement.js
import { format } from 'date-fns';
import { saveTableOrder, deleteTableOrder, loadTableOrders } from './OrderPersistence';
import { transactionService } from '../services/api';
import { createItemsWithAddons } from './transactionLines';

// Calculate total amounts for an order - UPDATED to be tenant-agnostic
const calculateTotals = (selectedProducts, addServiceCharge, discountAmount = 0, serviceChargeRate = 0.12, addTax = false, taxRate = 0) => {
    // Calculate the subtotal of all products including add-ons
    const subtotal = selectedProducts.reduce((total, product) => {
        const productTotal = (parseFloat(product.selling_price) || 0) * (parseInt(product.quantity) || 1);
        // Add the add-on total if present
        const addonTotal = product.add_ons
            ? product.add_ons.reduce((sum, addon) => sum + ((parseFloat(addon.price) || 0) * (parseInt(product.quantity) || 1)), 0)
            : 0;
        return total + productTotal + addonTotal;
    }, 0);

    // Calculate service charge using tenant-specific rate
    const serviceCharge = addServiceCharge ? subtotal * serviceChargeRate : 0;

    // Calculate tax
    const tax = addTax ? subtotal * taxRate : 0;

    // Calculate final total
    const total = Math.max(0, subtotal + serviceCharge + tax - (parseFloat(discountAmount) || 0));

    return {
        subtotal: parseFloat(subtotal.toFixed(2)),
        serviceCharge: parseFloat(serviceCharge.toFixed(2)),
        tax: parseFloat(tax.toFixed(2)),
        total: parseFloat(total.toFixed(2))
    };
};

// Update product quantities in inventory - ENHANCED with better error handling
const updateInventory = async (selectedProducts, products, productService, setProducts, selectedCategory, setFilteredProducts) => {
    try {
        const updatePromises = selectedProducts.map(async (product) => {
            const productToUpdate = products.find(p => p.product_code === product.product_code);
            if (productToUpdate) {
                // Skip inventory update for infinite quantity products
                if (productToUpdate.quantity === -9999) {
                    return; // Skip this product - don't update inventory for infinite products
                }

                const updatedQuantity = Math.max(0, productToUpdate.quantity - (parseInt(product.quantity) || 1));
                return productService.updateProduct(product.product_code, {
                    ...productToUpdate,
                    quantity: updatedQuantity
                });
            }
        });

        // Wait for all updates to complete
        await Promise.all(updatePromises.filter(Boolean));

        // Refresh product data
        const productData = await productService.getAllProducts();
        setProducts(productData);

        // Update filtered products if category is selected
        if (selectedCategory) {
            const filtered = productData.filter(product => product.category === selectedCategory);
            setFilteredProducts(filtered);
        }
    } catch (err) {
        console.error("Failed to update inventory:", err);
        throw err;
    }
};

const toggleProduct = (product, selectedProducts, setSelectedProducts) => {
    if (!product || !product.product_code) {
        console.error('Invalid product provided to toggleProduct');
        return;
    }

    // For products without add-ons, check if exact same product (same notes) exists
    const existingProductIndex = selectedProducts.findIndex(p => {
        if (p.product_code !== product.product_code) return false;

        // Check if both have no add-ons or empty add-ons
        const pAddons = p.add_ons || [];
        const productAddons = product.add_ons || [];

        // Check if notes are the same
        const pNotes = (p.notes || '').trim();
        const productNotes = (product.notes || '').trim();

        return pAddons.length === 0 && productAddons.length === 0 && pNotes === productNotes;
    });

    if (existingProductIndex >= 0) {
        // Update quantity if already selected with same configuration
        const updatedProducts = [...selectedProducts];
        updatedProducts[existingProductIndex].quantity = (updatedProducts[existingProductIndex].quantity || 0) + 1;
        setSelectedProducts(updatedProducts);
        console.log('Consolidated product in UI - new quantity:', updatedProducts[existingProductIndex].quantity);
    } else {
        // Add to selected products with safe defaults
        const newProduct = {
            ...product,
            quantity: 1,
            selling_price: parseFloat(product.selling_price) || 0,
            totalPrice: parseFloat(product.selling_price) || 0,
            notes: product.notes || '',
            add_ons: [], // Initialize empty add-ons array
            uniqueId: `${product.product_code}-no-addons-${Date.now()}`
        };

        setSelectedProducts([...selectedProducts, newProduct]);
        console.log('Added new product to UI:', newProduct.name);
    }
};

const updateProductNotes = (productId, notes, selectedProducts, setSelectedProducts) => {
    const updatedProducts = selectedProducts.map(product => {
        if ((product.uniqueId && product.uniqueId === productId) ||
            (!product.uniqueId && product.product_code === productId)) {
            return {
                ...product,
                notes: notes || ''
            };
        }
        return product;
    });

    setSelectedProducts(updatedProducts);
};

const updateProductQuantity = (productId, quantity, selectedProducts, setSelectedProducts) => {
    const hasUniqueIdMatch = selectedProducts.some(p => p.uniqueId && p.uniqueId === productId);

    const updatedProducts = selectedProducts.map(product => {
        const isMatch = hasUniqueIdMatch
            ? (product.uniqueId === productId)
            : (product.product_code === productId || product.id === productId);

        if (isMatch) {
            const newQuantity = Math.max(1, parseInt(quantity) || 1);
            const unitPrice = parseFloat(product.selling_price) || 0;
            return {
                ...product,
                quantity: newQuantity,
                totalPrice: unitPrice * newQuantity
            };
        }
        return product;
    });

    setSelectedProducts(updatedProducts);
};

const removeProduct = (productId, selectedProducts, setSelectedProducts) => {
    const hasUniqueIdMatch = selectedProducts.some(p => p.uniqueId && p.uniqueId === productId);

    setSelectedProducts(selectedProducts.filter(product => {
        const isMatch = hasUniqueIdMatch
            ? (product.uniqueId === productId)
            : (product.product_code === productId || product.id === productId);
        return !isMatch;
    }));
};

// Create transaction from order - ENHANCED with better data handling
//
// This is the PENDING-ORDER charge path (a table order being settled), shared
// by the web page (CashierSalesPage) and the native surface (pendingOrderFlow).
// It used to build `items` inline and FLAT: no line_id/parent_line_id/group_id,
// every add-on pushed as a sibling of the product. A nested modifier therefore
// arrived at POST /transactions/ parented to the product, so the server read
// the child's group as unanswered and rejected the whole sale with 400.
// It now emits the same hierarchy as the direct-sale path, via the one shared
// builder in ./transactionLines.
const createTransactionFromOrder = (order, date, salesDate = null) => {
    if (!order || !order.products) {
        throw new Error('Invalid order data provided');
    }

    // Drop malformed lines before building, preserving the old guard's warning.
    const validProducts = order.products.filter(product => {
        if (!product || !product.product_code) {
            console.warn('Skipping invalid product in order:', product);
            return false;
        }
        return true;
    });

    const items = createItemsWithAddons(validProducts);

    // Create transaction with safe defaults
    return {
        transaction_id: '', // Will be generated by backend
        date: format(date, 'yyyy-MM-dd'),
        time: format(new Date(), 'HH:mm:ss'),
        sales_date: salesDate || order.sales_date || format(date, 'yyyy-MM-dd'), // Use passed salesDate first, then order's salesDate, then fallback
        staff_name: order.cashier || 'Unknown',
        payment_method: order.paymentMethod || '',
        dining_option: order.diningOption || '',
        table_number: order.tableNumber || null,
        service_charge: Boolean(order.addServiceCharge),
        discount_amount: parseFloat(order.discount) || 0,
        total_amount: parseFloat(order.subtotal) || 0,
        final_amount: parseFloat(order.total) || 0,
        tax: parseFloat(order.tax) || 0,
        notes: order.notes || '',
        items: items,
        products: order.products, // Keep a reference to the original products
        // Promotion fields — present when a promo was applied at order creation time
        applied_promo_id: order.appliedPromoId || null,
        promo_discount_amount: parseFloat(order.promoDiscountAmount) || 0,
        // This order's stock left the shelf when it was HELD.
        //
        // Every caller of this builder is charging a PENDING order, and
        // holding one calls OrderHelpers.deductStockForPendingOrder, which
        // for a manual-stock product writes products.quantity straight down.
        // Without this the backend decrements the same unit again at
        // payment: BAC001 "Rice and Meat" went 9 -> 6 across two sales of
        // one unit each on depos_dev during the 9 Sep 2026 QC.
        //
        // Auto-stock products were never affected — their pending deduction
        // is LINKED to the sale rather than repeated (see
        // transactionFlow.linkInventoryToTransaction).
        stock_already_deducted: true,
    };
};

// Process order to transaction and update inventory - ENHANCED
const processOrderTransaction = async (transaction, order, products, productService, setProducts, selectedCategory, setFilteredProducts, loadTransactions, handlePrintFullReceipt) => {
    try {
        if (!transaction || !order) {
            throw new Error('Invalid transaction or order data');
        }

        // Create the transaction in the database
        const result = await transactionService.createTransaction(transaction);

        // Update inventory
        await updateInventory(
            order.products,
            products,
            productService,
            setProducts,
            selectedCategory,
            setFilteredProducts
        );

        // Print final customer/cashier receipt
        if (handlePrintFullReceipt) {
            setTimeout(() => {
                handlePrintFullReceipt();
            }, 300);
        }

        // Reload transactions
        if (loadTransactions) {
            await loadTransactions();
        }

        return result;
    } catch (err) {
        console.error('Failed to process order transaction', err);
        throw err;
    }
};

// Save the current order state for a specific table
const persistTableOrder = (tableNumber, orderData) => {
    if (tableNumber && orderData) {
        saveTableOrder(tableNumber, orderData);
    }
};

// Load a persisted order for a specific table
const loadPersistedTableOrder = (tableNumber) => {
    if (!tableNumber) return null;

    try {
        const tableOrders = loadTableOrders();
        return tableOrders[tableNumber] || null;
    } catch (err) {
        console.error('Failed to load persisted table order:', err);
        return null;
    }
};

// Clear a table's persisted order after it's processed
const clearTableOrderAfterProcessing = (tableNumber) => {
    if (tableNumber) {
        try {
            deleteTableOrder(tableNumber);
        } catch (err) {
            console.error('Failed to clear table order:', err);
        }
    }
};

export {
    calculateTotals,
    updateInventory,
    toggleProduct,
    updateProductQuantity,
    updateProductNotes,
    removeProduct,
    createTransactionFromOrder,
    processOrderTransaction,
    persistTableOrder,
    loadPersistedTableOrder,
    clearTableOrderAfterProcessing
};